import { BattleStatus, BattleUnit, Hex, FactionType, TurnPool, PathInfo } from '@/core/types/battle.types';
import { BattleMut, getInitialState } from '@/store/battleMutations';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

// 1. ІНТЕРФЕЙС ДАНИХ (Data Only)
export interface BattleData {
    status: BattleStatus;
    units: Map<string, BattleUnit>;
    battleMap: Map<string, Hex>;
    
    // UI state
    selectedUnitId: string | null;
    selectedSkillId: string | null;
    selectedHexId: string | null;
    selectedPath: PathInfo;
    inspectedUnitId: string | null;
    
    // Turn state
    round: number;
    activeFaction: FactionType;
    turnQueue: TurnPool[];
    finishedPools: TurnPool[];
    
    // Pathfinding state
    reachableHexes: string[];
    reachableHexesWithCost: Map<string, number>;
    targetHexes: string[];
    zocHexes: Set<string>;
}

// 2. ІНТЕРФЕЙС ДІЙ (Actions Only - сетери та мутації)
export interface BattleActions {
    // === SIMPLE SETTERS ===
    setStatus: (status: BattleStatus) => void;
    setSelectedUnit: (id: string | null) => void;
    setSelectedHex: (id: string | null) => void;
    setSelectedSkill: (id: string | null) => void;
    setSelectedPath: (pathInfo: PathInfo) => void;
    resetSelectedPath: () => void;
    
    setReachableHexes: (reachableIds: string[], reachableMap: Map<string, number>) => void;
    setTargetHexes: (hexes: string[]) => void;
    setZocHexes: (hexes: Set<string>) => void;
    
    // === UNITS ===
    addUnit: (unit: BattleUnit) => void;
    setUnits: (units: BattleUnit[]) => void;
    updateUnit: (id: string, updates: Partial<BattleUnit>) => void;
    removeUnit: (id: string) => void;
    setInspectedUnit: (id: string | null) => void;
    
    // === HEXES ===
    setBattleMap: (map: Map<string, Hex>) => void;
    updateHex: (hexId: string, data: Partial<Hex>) => void;
    
    // === TURN STATE ===
    setTurnQueue: (queue: TurnPool[]) => void;
    setRound: (round: number) => void;
    setActiveFaction: (faction: FactionType) => void;
    
    // === COMPLEX SETTERS ===
    applyUnitDeath: (data: { unitId: string; hexId: string; newQueue: TurnPool[] }) => void;
    apply: (fn: (state: BattleStore) => void) => void;
    reset: () => void;
}

// 3. ПОВНИЙ СТОР (Дані + Дії для Zustand)
export type BattleStore = BattleData & BattleActions;

// 4. ІНІЦІАЛІЗАЦІЯ СТОРУ
export const useBattleStore = create<BattleStore>()(
    devtools(
        subscribeWithSelector((set, get) => ({
            ...getInitialState(),

            // Generic apply - для композиції
            apply: (fn) => set((state) => {
                fn(state);        
                return { ...state }; 
            }),
            
            // Застосування мутацій через BattleMut
            updateHex: (id, patch) => set(state => {
                BattleMut.updateHex(state, id, patch);
                return { ...state };
            }),
            
            updateUnit: (id, patch) => set(state => {
                BattleMut.updateUnit(state, id, patch);
                return { ...state };
            }),
            
            resetSelectedPath: () => set(state => {
                BattleMut.resetSelectedPath(state);
                return { ...state };
            }),
            
            setReachableHexes: (hexes, costMap) => set(state => {
                BattleMut.setReachableHexes(state, hexes, costMap);
                return { ...state };
            }),

            // === SIMPLE SETTERS ===
            setStatus: (status) => set({ status }),
            setSelectedUnit: (id) => set({ selectedUnitId: id }),
            setSelectedHex: (id) => set({ selectedHexId: id, inspectedUnitId: null }),
            setSelectedSkill: (id) => set({ selectedSkillId: id, inspectedUnitId: null }),
            setSelectedPath: (pathInfo) => set({ selectedPath: pathInfo }),
            setInspectedUnit: (id) => set({ inspectedUnitId: id }),
            setTargetHexes: (hexes) => set({ targetHexes: hexes }),
            setZocHexes: (hexes) => set({ zocHexes: hexes }),
            
            // === UNITS ===
            addUnit: (unit) => set((state) => {
                const newUnits = new Map(state.units);
                newUnits.set(unit.unitId, unit);
                return { units: newUnits };
            }),

            setUnits: (unitsArray) => {
                const unitsMap = new Map();
                unitsArray.forEach(u => unitsMap.set(u.unitId, u));
                set({ units: unitsMap });
            },

            removeUnit: (id) => set((state) => {
                const newUnits = new Map(state.units);
                newUnits.delete(id);
                return { units: newUnits };
            }),
            
            // === HEXES ===
            setBattleMap: (map) => set({ battleMap: map }),

            // === COMPLEX SETTERS ===
            applyUnitDeath: (data) => set((state) => {
                const { unitId, hexId, newQueue } = data;
                
                // 1. Оновлюємо юніта
                const newUnits = new Map(state.units);
                const unit = newUnits.get(unitId);
                if (unit) {
                    newUnits.set(unitId, { 
                        ...unit, 
                        isDead: true, 
                        count: 0, 
                        currentUnitHp: 0 
                    });
                }

                // 2. Оновлюємо карту (звільняємо гекс)
                const newBattleMap = new Map(state.battleMap);
                const hex = newBattleMap.get(hexId);
                if (hex) {
                    newBattleMap.set(hexId, { ...hex, entityId: undefined });
                }

                return {
                    units: newUnits,
                    battleMap: newBattleMap,
                    turnQueue: newQueue,
                    selectedUnitId: state.selectedUnitId === unitId ? null : state.selectedUnitId
                };
            }),
            
            // === TURN STATE ===
            setTurnQueue: (queue) => set({ turnQueue: queue }),
            setRound: (round) => set({ round }),
            setActiveFaction: (faction) => set({ activeFaction: faction }),

            // === RESET ===
            reset: () => set(getInitialState()),
        })),
        { name: 'BattleGame', enabled: true }
    )
);